import { useContext, useMemo } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { findAirport, destinationTimesParser, compareDates } from 'helpers';
import { FlightStatusContext } from 'context';
import { FlightStatus } from 'components';
import { Location, Plane } from './components';
import {
  Container,
  Cell,
  Details,
  ColumnWrapper,
  General,
} from './Flight.style';

const Flight = ({ flight, id }) => {
  const { t } = useTranslation();
  const parsedFlight = id?.split('-');
  const {
    state: { airports, parsedDestinations },
    dispatch,
  } = useContext(FlightStatusContext);

  const handleModal = (id, show) => {
    dispatch({
      type: 'HANDLE_MODAL',
      payload: {
        open: show,
        id,
      },
    });
  };

  const departureLocation = useMemo(
    () =>
      parsedFlight &&
      airports &&
      parsedDestinations &&
      findAirport(parsedFlight[2], airports, parsedDestinations),
    [airports, parsedFlight, parsedDestinations]
  );

  const arrivalLocation = useMemo(
    () =>
      parsedFlight &&
      airports &&
      parsedDestinations &&
      findAirport(parsedFlight[3], airports, parsedDestinations),
    [airports, parsedFlight, parsedDestinations]
  );

  const locationTime = useMemo(
    () => destinationTimesParser(flight?.operationalTimes),
    [flight]
  );

  const isSameDate = useMemo(
    () =>
      flight?.departureDate?.dateLocal &&
      flight?.arrivalDate?.dateLocal &&
      compareDates(
        flight.departureDate.dateLocal,
        flight.arrivalDate.dateLocal
      ),
    [flight]
  );

  const anotherOperatedFlight = useMemo(() => {
    if (
      !parsedFlight[0].startsWith('SK') &&
      flight?.codeShares?.find((c) => c.startsWith('SK'))
    ) {
      return flight?.codeShares?.find((c) => c.startsWith('SK'));
    } else return '';
  }, [flight, parsedFlight]);

  return (
    <Container id={id}>
      <General>
        <ColumnWrapper>
          <Cell
            id="first-cell"
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="p">{parsedFlight[0]}</Typography>
            {anotherOperatedFlight && (
              <Typography variant="p" sx={{ mt: '5px' }}>
                {anotherOperatedFlight}
              </Typography>
            )}
          </Cell>
          <Cell id="second-cell">
            <Location
              locationTime={locationTime}
              location={departureLocation}
              direction="departure"
            />
          </Cell>
        </ColumnWrapper>
        <Cell id="third-cell">
          <Plane {...flight} />
        </Cell>
        <ColumnWrapper reverse="true" sx={{ maxWidth: '480px !important' }}>
          <Cell id="forth-cell">
            <Location
              locationTime={locationTime}
              location={arrivalLocation}
              direction="arrival"
              isSameDate={isSameDate}
            />
          </Cell>
          <Cell id="fifth-cell">
            <FlightStatus
              list={true}
              statusInfo={flight?.statusInfo}
              operated={flight?.operatedBy}
            />
          </Cell>
        </ColumnWrapper>
      </General>
      <Cell id="six-cell">
        <Details variant="p" onClick={() => handleModal(id, true)}>
          {t('flightstatus.results.details')}
        </Details>
      </Cell>
    </Container>
  );
};

export default Flight;
